/** Blocks saved before the Control Panel rejected these patterns still render, so the repair is a fallback — never silent, always paired with a `customHtmlNotices` notice. */

/** Both lists mirror the per-pattern rules in `apps/statamic/app/Rules/CustomHtml`. */
const strippedAttributes = ["data-component", "style"];
const strippedElements = ["script", "style"];

/** Mirrors `ScopedCss`: the css is rendered inside a `<style>`, so a `</style` would end it early. The rest of the end tag goes with it, or the leftover `>` reads as a child combinator and swallows the next rule. */
const styleElementEnd = /<\/style(?=[\s/>]|$)[^>]*>?/gi;

function removeComments(root: HTMLElement): void {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_COMMENT,
  );
  const comments: Comment[] = [];
  while (walker.nextNode()) {
    comments.push(walker.currentNode as Comment);
  }
  comments.forEach((comment) => comment.remove());
}

/** No browsing context, so parsing fetches nothing and runs no script; comments go first, since commented-out markup renders nothing to repair or warn about. */
function parseInertBody(html: string): HTMLElement {
  const parsed = document.implementation.createHTMLDocument();
  parsed.body.innerHTML = html;
  removeComments(parsed.body);
  return parsed.body;
}

function repairCustomHtml(html: string | null): string | null {
  if (!html) {
    return null;
  }
  const body = parseInertBody(html);
  body
    .querySelectorAll(strippedElements.join(", "))
    .forEach((element) => element.remove());
  strippedAttributes.forEach((attribute) => {
    body
      .querySelectorAll(`[${attribute}]`)
      .forEach((element) => element.removeAttribute(attribute));
  });
  return body.innerHTML;
}

/** Mirrors `ScopedCss::escapesScope`: the css renders inside `#id { … }`, so a brace closing no rule of its own ends that wrapper early. A string, a comment or an unquoted `url()` may hold one that counts for nothing. */
function scopeEscapes(css: string): number[] {
  const escapes: number[] = [];
  let depth = 0;

  for (let index = 0; index < css.length; index++) {
    const character = css[index];

    if (character === "\\") {
      index++;
    } else if (css.startsWith("/*", index)) {
      index = endOfBlockComment(css, index);
    } else if (character === '"' || character === "'") {
      index = endOfString(css, index);
    } else if (opensUnquotedUrl(css, index)) {
      index = endOfUrlToken(css, index);
    } else if (character === "{") {
      depth++;
    } else if (character === "}") {
      if (depth === 0) {
        escapes.push(index);
      } else {
        depth--;
      }
    }
  }

  return escapes;
}

function endOfBlockComment(css: string, index: number): number {
  const blockEnd = css.indexOf("*/", index + 2);

  return blockEnd === -1 ? css.length : blockEnd + 1;
}

function endOfString(css: string, index: number): number {
  const quote = css[index];

  for (let cursor = index + 1; cursor < css.length; cursor++) {
    if (css[cursor] === "\\") {
      cursor++;
    } else if (css[cursor] === quote) {
      return cursor;
    }
  }

  return css.length;
}

/** An unquoted `url()` runs verbatim to its parenthesis, so a data URI may hold braces; a quoted one the string scan covers. */
function opensUnquotedUrl(css: string, index: number): boolean {
  if (css.slice(index, index + 4).toLowerCase() !== "url(") {
    return false;
  }
  if (index > 0 && /[\w-]/.test(css.slice(index - 1, index))) {
    return false;
  }

  return !/^["']/.test(css.slice(index + 4).trimStart());
}

function endOfUrlToken(css: string, index: number): number {
  const urlEnd = css.indexOf(")", index);

  return urlEnd === -1 ? css.length : urlEnd;
}

/** Back to front, so removing one brace does not shift the index of the next. */
function withoutScopeEscapes(css: string): string {
  return scopeEscapes(css)
    .toReversed()
    .reduce(
      (stripped, index) => stripped.slice(0, index) + stripped.slice(index + 1),
      css,
    );
}

function stripStyleElementEnd(css: string): string {
  return css.replace(styleElementEnd, "");
}

function repairCustomCss(css: string | null): string | null {
  if (!css) {
    return null;
  }
  return withoutScopeEscapes(stripStyleElementEnd(css));
}

/** Derived from the strip, not a second `test()`: `styleElementEnd` is global, so `test()` would carry `lastIndex`. */
function endsStyleElement(css: string | null): boolean {
  return !!css && stripStyleElementEnd(css) !== css;
}

function escapesCssScope(css: string | null): boolean {
  return !!css && scopeEscapes(stripStyleElementEnd(css)).length > 0;
}

export {
  endsStyleElement,
  escapesCssScope,
  parseInertBody,
  repairCustomCss,
  repairCustomHtml,
};
